/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * ActivityView — Milestone 2 canvass activity list with filter/sort UI.
 *
 * Filter/sort spec: memory-bank/plans/filter-sort-ui-plan.md §5.4
 * API contract: memory-bank/plans/filter-sort-plan.md §5.6
 *
 * Layout:
 *   [H1: Canvass Activity] [Export CSV]
 *   [FilterBar: search, outcome, canvasser, precinct, route, date range]
 *   [Active filter chips + Clear all]
 *   [DataTable: Time | Canvasser | Voter | Address | Precinct | Outcome | Notes]
 *   [ResultsCount + pagination]
 *
 * Coordinator+ only. Returns 403 for canvassers.
 * URL-synced, server-side paginated (50 per page).
 */
import React, { useEffect, useState } from 'react';
import { Radio } from 'lucide-react';
import type { KnockbackActivityResponse, UserRole } from '@/types';
import { useListQuery } from '@/hooks/useListQuery';
import DataTable from '@/components/DataTable';
import EmptyState from '@/components/EmptyState';
import {
  FilterBar,
  SearchInput,
  FilterSelect,
  FilterMultiSelect,
  FilterDateRange,
} from '@/components/filters';
import client from '@/api/client';

interface PrecinctOption {
  value: string;
  label: string;
}

interface RouteOption {
  value: string;
  label: string;
}

// Outcome color palette — matches KnockbackForm.tsx
const OUTCOME_COLORS: Record<string, string> = {
  talked: 'bg-status-success-bg',
  not_home: 'bg-tier-warm',
  refused: 'bg-accent',
  moved: 'bg-border-subtle',
  come_back: 'bg-status-warning-bg',
  do_not_contact: 'bg-status-danger-bg',
};

const OUTCOME_OPTIONS = [
  { value: 'talked', label: 'Talked' },
  { value: 'not_home', label: 'Not Home' },
  { value: 'refused', label: 'Refused' },
  { value: 'moved', label: 'Moved' },
  { value: 'come_back', label: 'Come Back' },
  { value: 'do_not_contact', label: 'Do Not Contact' },
];

/**
 * ActivityView — server-paginated canvass activity log with filtering.
 * Coordinator+ only.
 */
export function ActivityView({ userRole }: { userRole?: UserRole }) {
  // ============================================================================
  // Access control
  // ============================================================================
  // Backend returns roles lowercase (`admin`, `coordinator`, ...) but we also
  // accept the uppercase form just in case a future /auth/me change normalizes.
  const roleNorm = typeof userRole === 'string' ? userRole.toUpperCase() : '';
  const isCoordinator = ['COORDINATOR', 'MANAGER', 'ORG_MANAGER', 'ADMIN'].includes(roleNorm);

  if (!isCoordinator) {
    return (
      <div className="min-h-dvh bg-bg-base text-fg-primary p-6">
        <div className="max-w-2xl mx-auto rounded-lg border border-status-danger-bg/30 bg-status-danger-bg/20 p-6">
          <h2 className="text-lg font-semibold text-status-danger mb-2">Access Denied</h2>
          <p className="text-fg-secondary">
            The Activity view is available to coordinators and above only. Canvassers can view
            knock activity from the Shift detail page.
          </p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // State for dropdowns (load from backend)
  // ============================================================================
  const [precinctOptions, setPrecinctOptions] = useState<PrecinctOption[]>([]);
  const [routeOptions, setRouteOptions] = useState<RouteOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  // ============================================================================
  // Query hook: server-side list query with envelope=v2
  // ============================================================================
  const query = useListQuery<KnockbackActivityResponse>({
    endpoint: '/canvass/knocks',
    initialParams: { envelope: 'v2' },
    pageSize: 50,
  });

  // ============================================================================
  // Load dropdown options on mount
  // ============================================================================
  useEffect(() => {
    async function loadOptions() {
      try {
        setLoadingOptions(true);

        // Load precinct and route options (always available).
        // NOTE: /users endpoint deferred to future slice; canvasser filter hidden below.
        const [precinctRes, routeRes] = await Promise.all([
          client.get('/voters/by-precinct', { params: { page_size: 500, envelope: 'v2' } }).catch(() => ({ data: { items: [] } })),
          client.get('/routes', { params: { page_size: 500, envelope: 'v2' } }).catch(() => ({ data: { items: [] } })),
        ]);

        // Map precinct response to options
        const precincts: PrecinctOption[] = (precinctRes.data.items || []).map((p: any) => ({
          value: String(p.precinct_num),
          label: `P${p.precinct_num}`,
        }));
        setPrecinctOptions(precincts);

        // Map route response to options
        const routes: RouteOption[] = (routeRes.data.items || []).map((r: any) => ({
          value: String(r.id),
          label: r.name || `Route ${r.id}`,
        }));
        setRouteOptions(routes);
      } catch (error) {
        console.error('Error loading filter options:', error);
      } finally {
        setLoadingOptions(false);
      }
    }

    loadOptions();
  }, []);

  // ============================================================================
  // Column definitions for DataTable
  // ============================================================================
  const columns = [
    {
      key: 'timestamp',
      header: 'Time',
      sortable: true,
      render: (row: KnockbackActivityResponse) => {
        const date = new Date(row.timestamp);
        return date.toLocaleString('en-US', {
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
      },
    },
    {
      key: 'canvasser_name',
      header: 'Canvasser',
      render: (row: KnockbackActivityResponse) => row.canvasser_name || `User #${row.canvasser_id}`,
    },
    {
      key: 'voter_last_name',
      header: 'Voter',
      render: (row: KnockbackActivityResponse) =>
        row.voter_first_name && row.voter_last_name
          ? `${row.voter_first_name} ${row.voter_last_name}`
          : `Voter #${row.voter_id}`,
    },
    {
      key: 'voter_address',
      header: 'Address',
      render: (row: KnockbackActivityResponse) => row.voter_address || '—',
    },
    {
      key: 'precinct_num',
      header: 'Precinct',
      render: (row: KnockbackActivityResponse) => (row.precinct_num ? `P${row.precinct_num}` : '—'),
    },
    {
      key: 'outcome',
      header: 'Outcome',
      render: (row: KnockbackActivityResponse) => {
        const color = OUTCOME_COLORS[row.outcome] || 'bg-border-subtle';
        const label =
          OUTCOME_OPTIONS.find((o) => o.value === row.outcome)?.label || row.outcome;
        return (
          <span className={`${color} text-white px-3 py-1 rounded-md text-sm font-medium`}>
            {label}
          </span>
        );
      },
    },
    {
      key: 'notes',
      header: 'Notes',
      render: (row: KnockbackActivityResponse) => {
        if (!row.notes) return '—';
        const truncated = row.notes.length > 60 ? `${row.notes.substring(0, 60)}…` : row.notes;
        return (
          <span title={row.notes} className="text-fg-muted text-sm cursor-help">
            {truncated}
          </span>
        );
      },
    },
  ];

  // ============================================================================
  // Handle export CSV
  // ============================================================================
  const handleExportCSV = async () => {
    // For now, just stub it with a "Coming soon" tooltip.
    // If /canvass/knocks/export exists, wire to that endpoint.
    alert('CSV export coming soon');
  };

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <div className="min-h-dvh bg-bg-base text-fg-primary p-6">
      <div className="max-w-full">
        {/* Header + Export button */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold">Canvass Activity</h1>
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 bg-status-success-bg hover:bg-status-success-bg/80 rounded-md text-white font-medium transition-colors"
            title="Coming soon"
          >
            Export CSV
          </button>
        </div>

        {/* FilterBar — NOTE: uses the current useListQuery API (search, filters,
            setSearch, setFilter, clearAll). An older version of this view
            referenced `query.params.q` / `query.clearFilters()` which don't
            exist on the hook and crashed the entire ActivityView render for
            admin/coordinator (session 5.9 diagnosis). */}
        <FilterBar>
          <SearchInput
            placeholder="Search voter name / address"
            value={query.search || ''}
            onChange={(value) => query.setSearch(value)}
          />

          <FilterMultiSelect
            label="Outcome"
            options={OUTCOME_OPTIONS}
            values={String(query.filters.outcome__in || '').split(',').filter(Boolean)}
            onChange={(values) =>
              query.setFilter('outcome__in', values.length > 0 ? values.join(',') : undefined)
            }
          />

          {/* FilterSelect for canvasser deferred to future slice pending /users endpoint. */}

          <FilterSelect
            label="Precinct"
            options={precinctOptions}
            value={query.filters.precinct_num ? String(query.filters.precinct_num) : null}
            onChange={(value) => query.setFilter('precinct_num', value || undefined)}
          />

          <FilterSelect
            label="Route"
            options={routeOptions}
            value={query.filters.route_id ? String(query.filters.route_id) : null}
            onChange={(value) => query.setFilter('route_id', value || undefined)}
          />

          {/* FilterDateRange expects value: [Date|null, Date|null] + onChange tuple.
              Map from the ISO strings we round-trip in query.filters. */}
          <FilterDateRange
            value={[
              query.filters.timestamp__gte ? new Date(String(query.filters.timestamp__gte)) : null,
              query.filters.timestamp__lte ? new Date(String(query.filters.timestamp__lte)) : null,
            ]}
            onChange={([from, to]) => {
              query.setFilter('timestamp__gte', from ? from.toISOString().split('T')[0] : undefined);
              query.setFilter('timestamp__lte', to ? to.toISOString().split('T')[0] : undefined);
            }}
          />
        </FilterBar>

        {/* Active filter chips */}
        {(Object.keys(query.filters).length > 0 || query.search) && (
          <div className="mb-4 flex flex-wrap gap-2">
            {query.search && (
              <span className="inline-flex items-center gap-2 px-3 py-1 bg-status-success-bg/40 border border-status-success-bg text-status-success rounded-full text-sm">
                q: {query.search}
                <button
                  onClick={() => query.setSearch('')}
                  className="text-status-success hover:text-fg-primary"
                >
                  ×
                </button>
              </span>
            )}
            {Object.entries(query.filters).map(([key, value]) => {
              if (key === 'envelope') return null; // Skip internal params
              if (value === undefined || value === null || value === '') return null;
              return (
                <span
                  key={key}
                  className="inline-flex items-center gap-2 px-3 py-1 bg-status-success-bg/40 border border-status-success-bg text-status-success rounded-full text-sm"
                >
                  {key}: {String(value)}
                  <button
                    onClick={() => query.setFilter(key, undefined)}
                    className="text-status-success hover:text-fg-primary"
                  >
                    ×
                  </button>
                </span>
              );
            })}
            <button
              onClick={() => query.clearAll()}
              className="text-status-success hover:text-status-success/80 text-sm underline"
            >
              Clear all
            </button>
          </div>
        )}

        {/* DataTable (or EmptyState when truly empty with no active filters) */}
        {!query.loading &&
        query.items.length === 0 &&
        !query.search &&
        Object.keys(query.filters).length === 0 ? (
          <EmptyState
            icon={Radio}
            title="Quiet for now"
            description="Canvassers' shift activity will show up here as it streams in."
          />
        ) : (
          <DataTable<KnockbackActivityResponse>
            columns={columns}
            query={query}
            rowKey={(row) => row.id}
            emptyMessage="No activity records found. Try broadening your filters."
            emptyFilteredMessage="No activity records match these filters."
            mobileMode="cards"
          />
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Helper functions for date presets
// ============================================================================

function getTodayISO(): string {
  const today = new Date();
  return today.toISOString().split('T')[0];
}

function getLast7dISO(): string {
  const date = new Date();
  date.setDate(date.getDate() - 7);
  return date.toISOString().split('T')[0];
}

function getLast30dISO(): string {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date.toISOString().split('T')[0];
}
